import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrderDetail, checkOrderPayment, checkOrderDrCare, updateOrderCosts, updateTracking, updateInternalNotes, refundOrder, voidOrderPayment } from '../../services/orderService';
import useToast from '../../hooks/useToast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { getSupplyLabel } from '../../utils/supplyTerms';
import { formatProviderLabel } from '../../utils/paymentProviders';
import styles from './OrderDetailPage.module.css';
import Modal from '../../components/common/Modal';
import OrderEconomicsPanel from '../../components/admin/OrderEconomicsPanel';
import { sumCostCents, resolveRevenueCents, formatCentsUsd as money } from './orderCostMath';

const TABS = ['Overview', 'Intake Form', 'DrCare', 'Payment', 'Activity Log'];

const STATUS_COLORS = {
  pending:        { bg: '#fef3cd', color: '#92610e', border: '#f9d77e' },
  under_review:   { bg: '#fff0e0', color: '#c2600a', border: '#f9c97e' },
  payment_held:   { bg: '#fff7ed', color: '#c2410c', border: '#fdba74' },
  approved:       { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
  denied:         { bg: '#fce4e4', color: '#b91c1c', border: '#f5a5a5' },
  processing:     { bg: '#fff4e6', color: '#b45309', border: '#fbbf6e' },
  shipped:        { bg: '#e0f2fe', color: '#0369a1', border: '#7dd3fc' },
  delivered:      { bg: '#dcfce7', color: '#15803d', border: '#86efac' },
  cancelled:      { bg: '#f3f0eb', color: '#78716c', border: '#d6d3cd' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.cancelled;
  return (
    <span
      className={styles.statusBadge}
      style={{ backgroundColor: s.bg, color: s.color, border: `0.5px solid ${s.border}` }}
    >
      {(status || '').replace(/_/g, ' ')}
    </span>
  );
};

const fmt = (dateStr) => {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

// `money` is imported from `./orderCostMath` (card 28334). It used to be a local copy that
// guessed the unit from the magnitude (`n > 999 ? n / 100 : n`); every field it is applied to on
// this page is integer CENTS - `orders.total`, `order_items.pricing_tier_price`,
// `order_payments.hold_amount`, `increased_strength_fee_cents` - so the guess only ever mis-scaled
// the sub-$10.00 rows. Sharing one formatter is what stops the page disagreeing with the module.

const g = (obj, ...keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v != null && v !== '') return v;
  }
  return null;
};


const FEE_STATUS_COLORS = {
  held:     { bg: '#fff7ed', color: '#c2410c', border: '#fdba74' },
  captured: { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
  voided:   { bg: '#f3f0eb', color: '#78716c', border: '#d6d3cd' },
  refunded: { bg: '#f3f0eb', color: '#78716c', border: '#d6d3cd' },
  failed:   { bg: '#fce4e4', color: '#b91c1c', border: '#f5a5a5' },
  none:     { bg: '#f3f0eb', color: '#78716c', border: '#d6d3cd' },
};

const FeeStatusBadge = ({ status }) => {
  const s = FEE_STATUS_COLORS[status] || FEE_STATUS_COLORS.none;
  return (
    <span
      className={styles.statusBadge}
      style={{ backgroundColor: s.bg, color: s.color, border: `0.5px solid ${s.border}` }}
    >
      {(status || 'none').replace(/_/g, ' ')}
    </span>
  );
};

const HigherDosageFeeCard = ({ request }) => {
  if (!request) return null;
  const meta = request.metadata || {};
  const originalLabel = meta.originalOrderNumber ?? meta.original_order_number ?? request.originalOrderId;
  const productName = meta.productName ?? meta.product_name;
  return (
    <div className={styles.infoCard}>
      <h3 className={styles.cardTitle}>Increased Strength Fee</h3>
      <div className={styles.infoRow}><span className={styles.label}>Fee Amount</span><span className={styles.value}>{money(request.increasedStrengthFeeCents)}</span></div>
      <div className={styles.infoRow}><span className={styles.label}>Fee Status</span><FeeStatusBadge status={request.feePaymentStatus} /></div>
      {/* These ids belong to higher_dosage_requests, which carries its OWN provider column
          (migration 074). Shown only when there is an id here to attribute. */}
      {(request.feeTransactionId || request.refundVoidTransactionId) && formatProviderLabel(request.paymentProvider) && (
        <div className={styles.infoRow}><span className={styles.label}>Provider</span><span className={styles.value}>{formatProviderLabel(request.paymentProvider)}</span></div>
      )}
      {request.feeTransactionId && (
        <div className={styles.infoRow}><span className={styles.label}>Fee Transaction ID</span><span className={styles.value}>{request.feeTransactionId}</span></div>
      )}
      <div className={styles.infoRow}><span className={styles.label}>Approval Status</span><StatusBadge status={request.approvalStatus} /></div>
      {request.providerDecisionAt && (
        <div className={styles.infoRow}><span className={styles.label}>Provider Decision</span><span className={styles.value}>{fmt(request.providerDecisionAt)}</span></div>
      )}
      {request.feeCapturedAt && (
        <div className={styles.infoRow}><span className={styles.label}>Fee Captured</span><span className={styles.value}>{fmt(request.feeCapturedAt)}</span></div>
      )}
      {request.feeVoidedAt && (
        <div className={styles.infoRow}><span className={styles.label}>Fee Voided</span><span className={styles.value}>{fmt(request.feeVoidedAt)}</span></div>
      )}
      {request.refundVoidTransactionId && (
        <div className={styles.infoRow}><span className={styles.label}>Refund/Void Transaction ID</span><span className={styles.value}>{request.refundVoidTransactionId}</span></div>
      )}
      {meta.dosage && (
        <div className={styles.infoRow}><span className={styles.label}>Requested Dosage</span><span className={styles.value}>{productName ? `${productName} — ${meta.dosage}` : meta.dosage}</span></div>
      )}
      {request.originalOrderId && (
        <div className={styles.infoRow}>
          <span className={styles.label}>Original Order</span>
          <span className={styles.value}>
            <a href={`/admin/orders/${request.originalOrderId}`} style={{ color: '#f97316', fontWeight: 600, textDecoration: 'none' }}>{originalLabel}</a>
          </span>
        </div>
      )}
    </div>
  );
};

const DN_STAGE_COLORS = {
  green:  '#22c55e',
  orange: '#f59e0b',
  red:    '#ef4444',
  gray:   '#d6d3cd',
};

const DN_STAGES = [
  { key: 'payment', label: 'Payment' },
  { key: 'encounter', label: 'Encounter' },
  { key: 'order', label: 'Order' },
  { key: 'fulfillment', label: 'Fulfillment' },
];

const DrNowLifecycleCard = ({ stages }) => {
  if (!stages) return null;
  return (
    <div className={styles.infoCard}>
      <h3 className={styles.cardTitle}>Dr. Now Lifecycle</h3>
      <div className={styles.dnLifecycleGrid}>
        {DN_STAGES.map(({ key, label }) => {
          const s = stages[key] || {};
          const color = DN_STAGE_COLORS[s.state] || DN_STAGE_COLORS.gray;
          return (
            <div key={key} className={styles.dnStage}>
              <div className={styles.dnStageHeader}>
                <span className={styles.dnStageDot} style={{ background: color }} />
                <span className={styles.dnStageLabel}>{label}</span>
                {s.breached && <span className={styles.dnBreachBadge}>SLA BREACHED</span>}
              </div>
              <div className={styles.infoRow}><span className={styles.label}>Started</span><span className={styles.value}>{fmt(s.startedAt)}</span></div>
              <div className={styles.infoRow}><span className={styles.label}>Completed</span><span className={styles.value}>{fmt(s.completedAt)}</span></div>
              <div className={styles.infoRow}><span className={styles.label}>SLA Deadline</span><span className={styles.value}>{fmt(s.slaDeadline)}</span></div>
              {s.failedAt && (
                <div className={styles.infoRow}><span className={styles.label}>Failed</span><span className={styles.value}>{fmt(s.failedAt)}</span></div>
              )}
              {s.webhookEventId && (
                <div className={styles.infoRow}><span className={styles.label}>Webhook Event</span><span className={styles.dnMetaValue}>{s.webhookEventId}</span></div>
              )}
              {s.transactionId && (
                <div className={styles.infoRow}><span className={styles.label}>Transaction</span><span className={styles.dnMetaValue}>{s.transactionId}</span></div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const calculateAge = (dob) => {
  if (!dob) return null;
  const birth = new Date(dob);
  const today = new Date();
  // `birth` is UTC midnight (DOB is date-only), so its calendar parts must be read in UTC;
  // `today` stays local because "today" is the operator's own calendar day.
  const hasHadBirthday = today.getMonth() > birth.getUTCMonth()
    || (today.getMonth() === birth.getUTCMonth() && today.getDate() >= birth.getUTCDate());
  return today.getFullYear() - birth.getUTCFullYear() - (hasHadBirthday ? 0 : 1);
};

const fmtDate = (d) => {
  if (!d) return '\u2014';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// `fmtDate` is CORRECT for timestamptz values (first_login_at, called_at, created_at): they
// carry a real time component and belong in the operator's local zone. A pg `date` column
// arrives as bare 'YYYY-MM-DD' = UTC midnight and needs the zone pinned, or it renders a day early.
const fmtDateOnly = (d) => {
  if (!d) return '\u2014';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

const PatientProfileCard = ({ user }) => {
  const [modalImage, setModalImage] = useState(null);
  const profile = user?.profile || {};
  const firstName = g(profile, 'firstName', 'first_name') || g(user, 'firstName', 'first_name') || '';
  const lastName = g(profile, 'lastName', 'last_name') || g(user, 'lastName', 'last_name') || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || '\u2014';
  const dob = g(profile, 'dateOfBirth', 'date_of_birth') || g(user, 'dateOfBirth', 'date_of_birth');
  const age = calculateAge(dob);
  const gender = g(profile, 'gender') || g(user, 'gender');
  const phone = g(profile, 'phone') || g(user, 'phone');
  const email = user?.email;
  const height = g(profile, 'height');
  const weight = g(profile, 'weight');
  const street = g(profile, 'street');
  const city = g(profile, 'city');
  const state = g(profile, 'state');
  const zipCode = g(profile, 'zipCode', 'zip_code');
  const addressParts = [street, [city, state].filter(Boolean).join(', '), zipCode].filter(Boolean);
  const telemedId = g(user, 'telemedId', 'telemed_id');
  const memberIdVal = g(profile, 'drcareMemberId', 'drcare_member_id') || g(user, 'drcareMemberId', 'drcare_member_id');
  const idVerification = g(profile, 'idVerification', 'id_verification');
  const frontUrl = idVerification?.frontUrl || idVerification?.front_url;
  const backUrl = idVerification?.backUrl || idVerification?.back_url;
  const firstLoginAt = g(user, 'firstLoginAt', 'first_login_at');
  const calledAt = g(user, 'calledAt', 'called_at');
  const calledStatus = g(user, 'calledStatus', 'called_status');
  const subscriptionStatus = g(user, 'subscriptionStatus', 'subscription_status');
  const subscriptionPlan = g(user, 'subscriptionPlan', 'subscription_plan');

  return (
    <>
      <div className={styles.infoCard}>
        <h3 className={styles.cardTitle}>Patient Profile</h3>
        <div className={styles.profileGrid}>
          <div className={styles.profileSection}>
            <div className={styles.infoRow}><span className={styles.label}>Name</span><span className={styles.value}>{fullName}</span></div>
            <div className={styles.infoRow}><span className={styles.label}>Email</span><span className={styles.value}>{email || '\u2014'}</span></div>
            <div className={styles.infoRow}><span className={styles.label}>Phone</span><span className={styles.value}>{phone || '\u2014'}</span></div>
            <div className={styles.infoRow}><span className={styles.label}>DOB</span><span className={styles.value}>{fmtDateOnly(dob)}{age != null ? ` (${age} yrs)` : ''}</span></div>
            <div className={styles.infoRow}><span className={styles.label}>Gender</span><span className={styles.value}>{gender || '\u2014'}</span></div>
            <div className={styles.infoRow}><span className={styles.label}>Height / Weight</span><span className={styles.value}>{height || weight ? [height, weight].filter(Boolean).join(' / ') : '\u2014'}</span></div>
            <div className={styles.infoRow}><span className={styles.label}>Address</span><span className={styles.value}>{addressParts.length > 0 ? addressParts.join(' ') : '\u2014'}</span></div>
          </div>
          <div className={styles.profileSection}>
            <div className={styles.infoRow}>
              <span className={styles.label}>Telemed ID</span>
              <span className={styles.value}>
                {telemedId ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
                    <span style={{ color: '#22c55e', fontWeight: 600 }}>{telemedId}</span>
                  </span>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
                    <span style={{ color: '#ef4444', fontWeight: 600 }}>Not Created</span>
                  </span>
                )}
              </span>
            </div>
            <div className={styles.infoRow}><span className={styles.label}>DrCare Member ID</span><span className={styles.value}>{memberIdVal || '\u2014'}</span></div>
            <div className={styles.infoRow}><span className={styles.label}>First Login</span><span className={styles.value}>{firstLoginAt ? fmtDate(firstLoginAt) : 'Never'}</span></div>
            <div className={styles.infoRow}><span className={styles.label}>Subscription</span><span className={styles.value}>{subscriptionPlan ? `${subscriptionPlan} (${subscriptionStatus || 'unknown'})` : 'No subscription'}</span></div>
            <div className={styles.infoRow}><span className={styles.label}>Called</span><span className={styles.value}>{calledStatus ? `${calledStatus}${calledAt ? ` \u2014 ${fmtDate(calledAt)}` : ''}` : 'Not called'}</span></div>
          </div>
        </div>
        <div className={styles.idSection}>
          <span className={styles.label} style={{ display: 'block', marginBottom: '0.75rem' }}>ID Verification</span>
          {frontUrl || backUrl ? (
            <div className={styles.idImages}>
              {frontUrl && (
                <div className={styles.idThumbWrap}>
                  <span className={styles.idLabel}>Front</span>
                  <img src={frontUrl} alt="License front" className={styles.idThumb} onClick={() => setModalImage(frontUrl)} />
                </div>
              )}
              {backUrl && (
                <div className={styles.idThumbWrap}>
                  <span className={styles.idLabel}>Back</span>
                  <img src={backUrl} alt="License back" className={styles.idThumb} onClick={() => setModalImage(backUrl)} />
                </div>
              )}
            </div>
          ) : (
            <p className={styles.noId}>No ID uploaded</p>
          )}
        </div>
      </div>
      <Modal isOpen={!!modalImage} onClose={() => setModalImage(null)} title="ID Verification" size="large">
        {modalImage && <img src={modalImage} alt="ID verification" className={styles.idFullImage} />}
      </Modal>
    </>
  );
};


/* ============================================
   Costs & Profit Card (editable)
   ============================================ */

const centsToStr = (c) => c != null && c > 0 ? (c / 100).toFixed(2) : '';
const strToCents = (s) => {
  const n = parseFloat(s);
  return isNaN(n) || n < 0 ? 0 : Math.round(n * 100);
};

const CostsCard = ({ order, orderId, onUpdate }) => {
  const { toast } = useToast();
  const [consultation, setConsultation] = useState(centsToStr(g(order, 'consultationCost', 'consultation_cost')));
  const [medication, setMedication] = useState(centsToStr(g(order, 'medicationCost', 'medication_cost')));
  const [misc, setMisc] = useState(centsToStr(g(order, 'miscCost', 'misc_cost')));
  const [shipping, setShipping] = useState(centsToStr(g(order, 'shippingAndHandlingCost', 'shipping_and_handling_cost')));
  const [saving, setSaving] = useState(false);

  // card 28329 - ALL FOUR stored cost columns, through the shared pure helper.
  // The sum used to be inlined here with only three, dropping shipping_and_handling_cost,
  // which rendered a short Total Cost and an OVERSTATED Profit. Do not re-inline it.
  const totalCostCents = sumCostCents([
    strToCents(consultation),
    strToCents(medication),
    strToCents(misc),
    strToCents(shipping)
  ]);
  const revenueCents = resolveRevenueCents(order);
  const profitCents = revenueCents - totalCostCents;

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateOrderCosts(orderId, {
        consultationCost: strToCents(consultation),
        medicationCost: strToCents(medication),
        miscCost: strToCents(misc),
        shippingAndHandlingCost: strToCents(shipping),
      });
      toast.success('Costs saved');
      if (onUpdate) onUpdate(res);
    } catch (e) {
      toast.error(e.message || 'Failed to save costs');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.infoCard}>
      <h3 className={styles.cardTitle}>Costs & Profit</h3>
      <div className={styles.costFields}>
        <div className={styles.costField}>
          <span className={styles.label}>Consultation Cost</span>
          <div className={styles.costInput}>
            <span className={styles.costPrefix}>$</span>
            <input type="number" min="0" step="0.01" placeholder="0.00" value={consultation} onChange={(e) => setConsultation(e.target.value)} className={styles.costInputEl} />
          </div>
        </div>
        <div className={styles.costField}>
          <span className={styles.label}>Medication Cost</span>
          <div className={styles.costInput}>
            <span className={styles.costPrefix}>$</span>
            <input type="number" min="0" step="0.01" placeholder="0.00" value={medication} onChange={(e) => setMedication(e.target.value)} className={styles.costInputEl} />
          </div>
        </div>
        <div className={styles.costField}>
          <span className={styles.label}>Misc Cost</span>
          <div className={styles.costInput}>
            <span className={styles.costPrefix}>$</span>
            <input type="number" min="0" step="0.01" placeholder="0.00" value={misc} onChange={(e) => setMisc(e.target.value)} className={styles.costInputEl} />
          </div>
        </div>
        <div className={styles.costField}>
          <span className={styles.label}>Shipping &amp; Handling Cost</span>
          <div className={styles.costInput}>
            <span className={styles.costPrefix}>$</span>
            <input type="number" min="0" step="0.01" placeholder="0.00" value={shipping} onChange={(e) => setShipping(e.target.value)} className={styles.costInputEl} />
          </div>
        </div>
      </div>
      <div className={styles.costSummary}>
        <div className={styles.infoRow}>
          <span className={styles.label}>Total Cost</span>
          <span className={styles.value}>{totalCostCents > 0 ? money(totalCostCents) : '\u2014'}</span>
        </div>
        <div className={styles.infoRow}>
          <span className={styles.label}>Profit</span>
          <span className={styles.value} style={{ color: profitCents < 0 ? '#b91c1c' : totalCostCents > 0 ? '#15803d' : undefined, fontWeight: 700 }}>
            {totalCostCents > 0 ? money(profitCents) : '\u2014'}
          </span>
        </div>
      </div>
      <button className={styles.saveCostsBtn} onClick={handleSave} disabled={saving}>
        {saving ? 'Saving\u2026' : 'Save Costs'}
      </button>
    </div>
  );
};


/* ============================================
   Tracking Card (editable)
   ============================================ */

const TrackingCard = ({ order, orderId }) => {
  const { toast } = useToast();
  const [trackingNumber, setTrackingNumber] = useState(g(order, "trackingNumber", "tracking_number") || "");
  const [trackingUrl, setTrackingUrl] = useState(g(order, "trackingUrl", "tracking_url") || "");
  const [carrier, setCarrier] = useState(g(order, "carrier") || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateTracking(orderId, { trackingNumber, trackingUrl, carrier });
      toast.success("Tracking info saved");
    } catch (e) {
      toast.error(e.message || "Failed to save tracking");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.infoCard}>
      <h3 className={styles.cardTitle}>Tracking Info</h3>
      <div className={styles.costFields}>
        <div className={styles.costField}>
          <span className={styles.label}>Tracking Number</span>
          <input type="text" placeholder="e.g. 1Z999AA10123456784" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} className={styles.costInputEl} style={{ paddingLeft: "12px" }} />
        </div>
        <div className={styles.costField}>
          <span className={styles.label}>Carrier</span>
          <input type="text" placeholder="e.g. UPS, FedEx, USPS" value={carrier} onChange={(e) => setCarrier(e.target.value)} className={styles.costInputEl} style={{ paddingLeft: "12px" }} />
        </div>
        <div className={styles.costField}>
          <span className={styles.label}>Tracking URL</span>
          <input type="text" placeholder="https://..." value={trackingUrl} onChange={(e) => setTrackingUrl(e.target.value)} className={styles.costInputEl} style={{ paddingLeft: "12px" }} />
        </div>
      </div>
      <button className={styles.saveCostsBtn} onClick={handleSave} disabled={saving}>
        {saving ? "Saving\u2026" : "Save Tracking"}
      </button>
    </div>
  );
};

/* ============================================
   Internal Notes Card (editable)
   ============================================ */

const NotesCard = ({ order, orderId }) => {
  const { toast } = useToast();
  const [notes, setNotes] = useState(g(order, "internalNotes", "internal_notes") || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateInternalNotes(orderId, notes);
      toast.success("Notes saved");
    } catch (e) {
      toast.error(e.message || "Failed to save notes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.infoCard}>
      <h3 className={styles.cardTitle}>Internal Notes</h3>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Add internal notes about this order..."
        rows={4}
        style={{ width: "100%", padding: "10px 12px", border: "0.5px solid var(--color-tan-300)", borderRadius: "8px", fontFamily: "inherit", fontSize: "0.875rem", resize: "vertical", background: "var(--color-cream, #f9f6f1)" }}
      />
      <button className={styles.saveCostsBtn} onClick={handleSave} disabled={saving} style={{ marginTop: "12px" }}>
        {saving ? "Saving\u2026" : "Save Notes"}
      </button>
    </div>
  );
};

/* ============================================
   Order Actions (Refund, Void)
   ============================================ */

const OrderActionsCard = ({ data, orderId }) => {
  const { toast } = useToast();
  const [refunding, setRefunding] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const payments = data.payments || [];
  const order = data.order || {};
  const paymentTxnId = payments.find(p => p.authorize_transaction_id)?.authorize_transaction_id;
  const orderHoldTxnId = order.order_hold_transaction_id;
  const hasRefundablePayment = !!(paymentTxnId || orderHoldTxnId);
  const holdTxnId = order.consultation_hold_transaction_id || order.order_hold_transaction_id;
  const holdStatus = g(order, "holdStatus", "hold_status");
  const canVoid = holdTxnId && holdStatus === "held";

  const handleRefund = async () => {
    const payment = payments.find(p => p.authorize_transaction_id);
    const amount = payment ? (payment.amount || payment.hold_amount) : (order.total || 0);
    const confirmed = window.confirm(
      `Are you sure you want to refund $${(amount / 100).toFixed(2)} for this order? This action cannot be undone.`
    );
    if (!confirmed) return;

    setRefunding(true);
    try {
      await refundOrder(orderId, amount);
      toast.success("Refund processed successfully");
    } catch (e) {
      toast.error(e.message || "Refund failed");
    } finally {
      setRefunding(false);
    }
  };

  const handleVoid = async () => {
    const txnId = order.order_hold_transaction_id || order.consultation_hold_transaction_id;
    const confirmed = window.confirm(
      "Are you sure you want to void this payment hold? This will release the held funds."
    );
    if (!confirmed) return;

    setVoiding(true);
    try {
      await voidOrderPayment(orderId, txnId);
      toast.success("Payment hold voided successfully");
    } catch (e) {
      toast.error(e.message || "Void failed");
    } finally {
      setVoiding(false);
    }
  };

  if (!hasRefundablePayment && !canVoid) return null;

  return (
    <div className={styles.infoCard}>
      <h3 className={styles.cardTitle}>Order Actions</h3>
      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
        {hasRefundablePayment && (
          <button
            onClick={handleRefund}
            disabled={refunding}
            style={{ padding: "8px 20px", fontSize: "0.875rem", fontWeight: 600, color: "#fff", background: "#ef4444", border: "none", borderRadius: "8px", cursor: "pointer", opacity: refunding ? 0.6 : 1 }}
          >
            {refunding ? "Processing\u2026" : "Refund Payment"}
          </button>
        )}
        {canVoid && (
          <button
            onClick={handleVoid}
            disabled={voiding}
            style={{ padding: "8px 20px", fontSize: "0.875rem", fontWeight: 600, color: "#78716c", background: "#f3f0eb", border: "0.5px solid #d6d3cd", borderRadius: "8px", cursor: "pointer", opacity: voiding ? 0.6 : 1 }}
          >
            {voiding ? "Processing\u2026" : "Void Payment Hold"}
          </button>
        )}
      </div>
    </div>
  );
};

/* ============================================
   Overview Tab
   ============================================ */

const OverviewTab = ({ data }) => {
  const { order, statusHistory, shippingAddresses } = data;
  const addr = shippingAddresses?.[0];
  const userName = g(order?.user, 'fullName') ||
    [g(order?.user, 'firstName', 'first_name'), g(order?.user, 'lastName', 'last_name')].filter(Boolean).join(' ') ||
    '\u2014';
  const memberIdVal = g(order?.user, 'drcareMemberId', 'drcare_member_id') ||
    g(order?.user?.profile, 'drcareMemberId', 'drcare_member_id');
  const holdVal = g(order, 'holdStatus', 'hold_status');
  const trackingNumberVal = g(order, 'trackingNumber', 'tracking_number');
  const trackingUrlVal = g(order, 'trackingUrl', 'tracking_url');
  const carrierVal = g(order, 'carrier');

  return (
    <div className={styles.tabContent}>
      <div className={styles.overviewGrid}>
        <div className={styles.infoCard}>
          <h3 className={styles.cardTitle}>Order Info</h3>
          <div className={styles.infoRow}><span className={styles.label}>Order Number</span><span className={styles.value}>{g(order, 'orderNumber', 'order_number') || '\u2014'}</span></div>
          <div className={styles.infoRow}><span className={styles.label}>Status</span><StatusBadge status={order?.status} /></div>
          <div className={styles.infoRow}><span className={styles.label}>Created</span><span className={styles.value}>{fmt(g(order, 'createdAt', 'created_at'))}</span></div>
          <div className={styles.infoRow}><span className={styles.label}>Total</span><span className={styles.value}>{money(order?.total)}</span></div>
          {trackingNumberVal && (
            <div className={styles.infoRow}>
              <span className={styles.label}>Tracking Number</span>
              <span className={styles.value}>
                {trackingUrlVal
                  ? <a href={trackingUrlVal} target="_blank" rel="noopener noreferrer" style={{ color: '#f97316', fontWeight: 600, textDecoration: 'none' }}>{trackingNumberVal}</a>
                  : trackingNumberVal}
              </span>
            </div>
          )}
          {carrierVal && (
            <div className={styles.infoRow}><span className={styles.label}>Carrier</span><span className={styles.value}>{carrierVal}</span></div>
          )}
          {holdVal && holdVal !== 'none' && (
            <div className={styles.infoRow}><span className={styles.label}>Hold Status</span><span className={styles.value}>{holdVal.replace(/_/g, ' ')}</span></div>
          )}
          {g(order, "preferredAppointmentTime", "preferred_appointment_time") && (
            <div className={styles.infoRow}><span className={styles.label}>Appointment</span><span className={styles.value}>{fmt(g(order, "preferredAppointmentTime", "preferred_appointment_time"))}</span></div>
          )}
          {g(order, "reminder30mSentAt", "reminder_30m_sent_at") && (
            <div className={styles.infoRow}><span className={styles.label}>30-Min Reminder</span><span className={styles.value}>{fmt(g(order, "reminder30mSentAt", "reminder_30m_sent_at"))}</span></div>
          )}
          {g(order, "reminderAtTimeSentAt", "reminder_at_time_sent_at") && (
            <div className={styles.infoRow}><span className={styles.label}>At-Time Reminder</span><span className={styles.value}>{fmt(g(order, "reminderAtTimeSentAt", "reminder_at_time_sent_at"))}</span></div>
          )}
          {(g(order, 'supplyMonths', 'supply_months') || 0) > 0 && (
            <div className={styles.infoRow}><span className={styles.label}>Supply Duration</span><span className={styles.value}>{getSupplyLabel(g(order, 'supplyMonths', 'supply_months'))}</span></div>
          )}
          {order?.metadata?.fulfillment_schedule && (
            <div className={styles.infoRow}>
              <span className={styles.label}>Fulfillment</span>
              <span className={styles.value}>
                {order.metadata.fulfillment_description || `${order.metadata.total_shipments} shipments`}
              </span>
            </div>
          )}
          {order?.metadata?.fulfillment_schedule && (
            <div style={{ padding: '8px 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
              {order.metadata.fulfillment_schedule.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 0' }}>
                  <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: i === 0 ? '#22c55e' : '#d1d5db', flexShrink: 0 }} />
                  <span>Shipment {s.shipment_number || s.shipmentNumber}: {s.months}-month supply {i === 0 ? '(ships immediately)' : '(scheduled)'}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.infoCard}>
          <h3 className={styles.cardTitle}>Customer</h3>
          <div className={styles.infoRow}><span className={styles.label}>Name</span><span className={styles.value}>{userName}</span></div>
          <div className={styles.infoRow}><span className={styles.label}>Email</span><span className={styles.value}>{order?.user?.email || '\u2014'}</span></div>
          <div className={styles.infoRow}>
            <span className={styles.label}>Telemed ID</span>
            <span className={styles.value}>
              {(order?.user?.telemedId || memberIdVal) ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e' }} />
                  <span style={{ color: '#22c55e', fontWeight: 600 }}>Verified</span>
                  <span style={{ color: '#888', fontSize: '12px', marginLeft: '4px' }}>{order?.user?.telemedId || memberIdVal}</span>
                </span>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444' }} />
                  <span style={{ color: '#ef4444', fontWeight: 600 }}>Not Created</span>
                </span>
              )}
            </span>
          </div>
          {order?.user?.subscriptionPlan && (
            <div className={styles.infoRow}><span className={styles.label}>Plan</span><span className={styles.value}>{order.user.subscriptionPlan} ({order.user.subscriptionStatus || 'unknown'})</span></div>
          )}
        </div>

        {addr && (
          <div className={styles.infoCard}>
            <h3 className={styles.cardTitle}>Shipping Address</h3>
            <div className={styles.addressBlock}>
              <div>{g(addr, 'fullName', 'full_name') || [g(addr, 'first_name', 'firstName'), g(addr, 'last_name', 'lastName')].filter(Boolean).join(' ')}</div>
              <div>{g(addr, 'addressLine1', 'address_line_1', 'street', 'street1')}</div>
              {g(addr, 'addressLine2', 'address_line_2', 'street2') && <div>{g(addr, 'addressLine2', 'address_line_2', 'street2')}</div>}
              <div>{addr.city}, {addr.state} {g(addr, 'zipCode', 'zip_code', 'zip')}</div>
            </div>
          </div>
        )}
      </div>

      <DrNowLifecycleCard stages={order?.lifecycleStages} />

      <PatientProfileCard user={order?.user} />

      <CostsCard order={order} orderId={g(order, 'id')} onUpdate={(res) => { if (res?.order) { Object.assign(order, res.order); } }} />

      {/* Stored canonical economics beside the preserved original. Sits next to CostsCard
          deliberately: CostsCard edits the ORDER's live cost columns (integer cents), this
          panel reads the versioned stored rows (dollars). Same subject, different quantities. */}
      <OrderEconomicsPanel view={data.economics} />

      <HigherDosageFeeCard request={data.higherDosageRequest} />

      <TrackingCard order={order} orderId={g(order, 'id')} />
      <NotesCard order={order} orderId={g(order, 'id')} />
      <OrderActionsCard data={data} orderId={g(order, 'id')} />

      {data.items?.length > 0 && (
        <div className={styles.infoCard}>
          <h3 className={styles.cardTitle}>Items</h3>
          <div className={styles.itemsList}>
            {data.items.map((item, i) => (
              <div key={i} className={styles.orderItem}>
                <span className={styles.itemName}>{g(item, 'productName', 'product_name') || item.products?.name || 'Unknown Product'}</span>
                <span className={styles.itemDetail}>{g(item, 'dosageName', 'dosage', 'pricing_tier_name') || ''} {item.quantity > 1 ? `x${item.quantity}` : ''}</span>
                <span className={styles.itemPrice}>{money(g(item, 'price', 'pricing_tier_price', 'unitPrice'))}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.infoCard}>
        <h3 className={styles.cardTitle}>Status Timeline</h3>
        {statusHistory?.length > 0 ? (
          <div className={styles.timeline}>
            {statusHistory.map((entry, i) => (
              <div key={i} className={styles.timelineEntry}>
                <div className={styles.timelineDot} />
                <div className={styles.timelineContent}>
                  <StatusBadge status={g(entry, 'status', 'newStatus', 'new_status')} />
                  <span className={styles.timelineDate}>{fmt(g(entry, 'createdAt', 'created_at'))}</span>
                  {entry.note && <p className={styles.timelineNote}>{entry.note}</p>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.emptyMessage}>No status history available</p>
        )}
      </div>
    </div>
  );
};

/* ============================================
   Intake Form Tab
   ============================================ */

const parseQA = (qa) => {
  // Handle case where answer is stringified JSON with {label, value, fieldId}
  let question = qa.question || qa.label;
  let answer = qa.answer;
  if (typeof answer === 'string' && answer.startsWith('{')) {
    try {
      const parsed = JSON.parse(answer);
      if (parsed.label) question = parsed.label;
      if (parsed.value !== undefined) answer = parsed.value;
    } catch (_) {}
  }
  // Format answer for display
  if (Array.isArray(answer)) {
    answer = answer.map(v => typeof v === 'string' ? v.replace(/_/g, ' ') : v).join(', ');
  } else if (typeof answer === 'object' && answer !== null) {
    // Handle {feet, inches} and similar structured values
    if (answer.feet !== undefined) {
      answer = `${answer.feet}'${answer.inches || 0}"`;
    } else {
      answer = JSON.stringify(answer);
    }
  } else if (typeof answer === 'boolean') {
    answer = answer ? 'Yes' : 'No';
  } else {
    answer = String(answer ?? '\u2014').replace(/_/g, ' ');
  }
  return { question: question || 'Unknown', answer };
};

const IntakeFormTab = ({ data }) => {
  const submissions = data.intakeSubmissions || data.intake_submissions || [];
  const [expandedQ, setExpandedQ] = useState({});

  if (submissions.length === 0) {
    return <div className={styles.tabContent}><div className={styles.infoCard}><p className={styles.emptyMessage}>No intake submission found</p></div></div>;
  }

  const toggleQ = (si, qi) => setExpandedQ(prev => ({ ...prev, [`${si}-${qi}`]: !prev[`${si}-${qi}`] }));

  return (
    <div className={styles.tabContent}>
      {submissions.map((sub, si) => {
        const prodName = g(sub, 'productName', 'product_name');
        const responses = sub.formattedResponses || sub.formatted_responses || sub.responses || [];
        const parsed = Array.isArray(responses) ? responses.map(parseQA) : [];

        return (
          <div key={si} className={styles.infoCard}>
            <h3 className={styles.cardTitle}>
              Intake Form{submissions.length > 1 ? ` #${si + 1}` : ''}
              {prodName && <span className={styles.subtitle}>{` \u2014 ${prodName}`}</span>}
            </h3>
            <div className={styles.intakeResponses}>
              {parsed.length > 0 ? parsed.map((qa, qi) => {
                const isOpen = expandedQ[`${si}-${qi}`];
                return (
                  <div key={qi} className={styles.qaAccordion} onClick={() => toggleQ(si, qi)} style={{ cursor: 'pointer' }}>
                    <div className={styles.qaHeader}>
                      <span className={styles.qaToggle}>{isOpen ? '\u25BC' : '\u25B6'}</span>
                      <span className={styles.qaNum}>{qi + 1}</span>
                      <span className={styles.question}>{qa.question}</span>
                    </div>
                    {isOpen && (
                      <div className={styles.qaBody}>
                        <div className={styles.answer}>{qa.answer}</div>
                      </div>
                    )}
                  </div>
                );
              }) : <p className={styles.emptyMessage}>No responses recorded</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ============================================
   Collapsible Code Block
   ============================================ */

const CollapsibleCode = ({ title, json, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    const text = typeof json === 'string' ? json : JSON.stringify(json, null, 2);
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }).catch(() => {});
        return;
      }
    } catch (_) {}
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={styles.collapsibleBlock}>
      <div className={styles.collapsibleHeader} onClick={() => setOpen(!open)}>
        <span>{open ? '▼' : '▶'} {title}</span>
        <button className={styles.copyBtn} onClick={handleCopy}>
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      {open && (
        <pre className={styles.darkCodeBlock}>
          {typeof json === 'string' ? json : JSON.stringify(json, null, 2)}
        </pre>
      )}
    </div>
  );
};

/* ============================================
   DrCare Tab
   ============================================ */

const DrCareTab = ({ data, orderId }) => {
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const snap = data.snapshots?.[0];
  const payload = snap?.drcare_payload;
  const response = snap?.drcare_response;
  const drcareLogs = data.drcareLogs || [];
  const hasPayload = !!payload;
  const hasResponse = !!response;
  const hasLogs = drcareLogs.length > 0;
  const appointmentId = response?.data?.id || snap?.drcare_appointment_id || g(data.order, 'drcareAppointmentId', 'drcare_appointment_id');
  const consultationNumber = response?.data?.consultationNumber || response?.data?.consultation_number;

  // Card 54201 -- the server's derived verdict, so the admin never has to read raw logs to
  // learn an appointment was never created. All five keys are always present (see
  // `deriveDrCareAppointmentState` in orderDetailController.js), so `state` alone decides.
  const appointment = data.drcareAppointment;
  // `fmtDate` above is date-ONLY; a refused appointment slot is only actionable with its
  // time of day, so this panel formats its own. Kept local -- nothing else needs it.
  const fmtWhen = (d) => (d
    ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })
    : '\u2014');

  const handleCheck = async () => {
    setLoading(true);
    setErr(null);
    try { setLive(await checkOrderDrCare(orderId)); }
    catch (e) { setErr(e.message || 'Failed to check DrCare status'); }
    finally { setLoading(false); }
  };

  return (
    <div className={styles.tabContent}>
      {appointment?.state === 'failed' && (
        <div
          data-testid="drcare-failure-banner"
          role="alert"
          style={{ background: '#fef2f2', border: '0.5px solid #fca5a5', borderRadius: '12px', padding: '1.25rem' }}
        >
          <h3 className={styles.cardTitle} style={{ color: '#b91c1c', margin: '0 0 1rem 0' }}>
            DrCare appointment was not created
          </h3>
          <div className={styles.infoRow}>
            <span className={styles.label}>Reason</span>
            <span className={styles.value}>{appointment.message || 'No reason was reported by DrCare'}</span>
          </div>
          {appointment.code && (
            <div className={styles.infoRow}>
              <span className={styles.label}>Vendor code</span>
              <span className={styles.value} style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{appointment.code}</span>
            </div>
          )}
          <div className={styles.infoRow}>
            <span className={styles.label}>Time attempted</span>
            <span className={styles.value}>{fmtWhen(appointment.attemptedScheduleOnUtc)}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.label}>Last attempt</span>
            <span className={styles.value}>{fmtWhen(appointment.lastAttemptAt)}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.label}>Next action</span>
            <span className={styles.value}>
              The payment went through and was deliberately left untouched. Book this appointment
              manually in DrCare, then record the appointment ID on this order.
            </span>
          </div>
        </div>
      )}

      {(hasPayload || hasResponse) && (() => {
        // Support both old format (body-only) and new format ({url, method, headers, body})
        const isFullEnvelope = payload && payload.url && payload.headers;
        const requestUrl = isFullEnvelope ? payload.url : null;
        const requestMethod = isFullEnvelope ? (payload.method || 'POST') : 'POST';
        const requestHeaders = isFullEnvelope ? payload.headers : null;
        const requestBody = isFullEnvelope ? payload.body : payload;
        const isReconstructed = isFullEnvelope && payload._reconstructed;
        return (
        <div className={styles.infoCard}>
          <h3 className={styles.cardTitle}>DrCare API Request</h3>
          {isReconstructed && (
            <p style={{ color: 'var(--color-gray-400)', fontSize: '0.75rem', fontStyle: 'italic', margin: '0 0 0.75rem' }}>
              Headers reconstructed from server config
            </p>
          )}
          <div className={styles.infoRow}>
            <span className={styles.label}>Endpoint</span>
            <span className={styles.value} style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
              {requestMethod} {requestUrl || '/v1/Schedule/createappointment'}
            </span>
          </div>
          {appointmentId && (
            <div className={styles.infoRow}>
              <span className={styles.label}>Appointment ID</span>
              <span className={styles.value} style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{appointmentId}</span>
            </div>
          )}
          {consultationNumber && (
            <div className={styles.infoRow}>
              <span className={styles.label}>Consultation #</span>
              <span className={styles.value}>{consultationNumber}</span>
            </div>
          )}
          {requestHeaders && <CollapsibleCode title="Request Headers" json={requestHeaders} />}
          {requestBody && <CollapsibleCode title="Request Body" json={requestBody} />}
          {!requestHeaders && hasPayload && !isFullEnvelope && <CollapsibleCode title="Request Body" json={payload} />}
          {hasResponse && <CollapsibleCode title="Response" json={response} />}
        </div>
        );
      })()}

      {!hasPayload && !hasResponse && hasLogs && (
        <div className={styles.infoCard}>
          <h3 className={styles.cardTitle}>DrCare API Request</h3>
          <p style={{ color: 'var(--color-gray-500)', fontSize: '0.85rem', margin: '0 0 1rem' }}>
            No snapshot payload found. Showing data from activity logs.
          </p>
          {drcareLogs.map((log, i) => (
            <div key={log.id || i} style={{ marginBottom: '1rem' }}>
              <div className={styles.infoRow}>
                <span className={styles.label}>Log Entry</span>
                <span className={styles.value}>{fmtDate(log.created_at || log.createdAt)}</span>
              </div>
              {log.details && <CollapsibleCode title={`Log Details #${i + 1}`} json={log.details} />}
              {log.request && <CollapsibleCode title={`Log Request #${i + 1}`} json={log.request} />}
              {log.response && <CollapsibleCode title={`Log Response #${i + 1}`} json={log.response} />}
            </div>
          ))}
        </div>
      )}

      {!hasPayload && !hasResponse && !hasLogs && (
        <div className={styles.infoCard}>
          <h3 className={styles.cardTitle}>DrCare API Request</h3>
          <p className={styles.notSubmitted}>Not Submitted</p>
          <p style={{ color: 'var(--color-gray-500)', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>
            This order has not been submitted to DrCare. No payload, response, or submission logs were found.
          </p>
        </div>
      )}

      <div className={styles.infoCard}>
        <h3 className={styles.cardTitle}>Live Status</h3>
        <button className={styles.checkBtn} onClick={handleCheck} disabled={loading}>
          {loading ? 'Checking…' : 'Check Live Status'}
        </button>
        {err && <div className={styles.checkError}>{err}</div>}
        {live && <pre className={styles.jsonPre}>{JSON.stringify(live, null, 2)}</pre>}
      </div>
    </div>
  );
};

/* ============================================
   Payment Tab
   ============================================ */

const PaymentTab = ({ data, orderId }) => {
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const payments = data.payments || [];
  const order = data.order || {};

  // A Test Admin Dashboard order carries metadata.sandbox (stampSandbox, api/utils/sandboxMode.js).
  // Its gateway references are SANDBOX-<uuid> strings minted by the simulator, not Authorize.Net
  // ids: nothing was charged, held, voided or refunded. The admin ORDER LIST already hides these
  // rows (orderController.listOrders passes excludeSandbox: true), but this detail page is
  // reachable by direct link and rendered them as ordinary transaction ids.
  const isSandboxOrder = order.metadata?.sandbox === true;
  const sandboxNotice = isSandboxOrder ? (
    <div className={styles.sandboxNotice} data-testid="sandbox-payment-notice">
      SANDBOX - synthetic reference. No card was charged and no money moved.
    </div>
  ) : null;

  const handleCheck = async () => {
    setLoading(true);
    setErr(null);
    try { setLive(await checkOrderPayment(orderId)); }
    catch (e) { setErr(e.message || 'Failed to check payment status'); }
    finally { setLoading(false); }
  };

  const paymentFields = (p) => [
    ['Method', p.method],
    ['Status', p.status],
    // Named from the OWNING order_payments row. The existing `v != null` filter below drops the
    // row if a provider is ever absent, so no new empty state is introduced (#53296).
    ['Provider', formatProviderLabel(p.payment_provider)],
    // ⚠️ READ BOTH ID COLUMNS — same `??` order as orderDetailController.js:296.
    // `authorize_transaction_id` is LEGACY and was NULL on all 244 live rows measured
    // 2026-09-20 (220 carry `transaction_id`), so reading it alone rendered NO id at all.
    ['Transaction ID', p.authorize_transaction_id ?? p.transaction_id],
    ['Hold Status', p.hold_status],
    ['Hold Amount', p.hold_amount != null ? money(p.hold_amount) : null],
    ['Card', p.card_last4 ? `${p.card_brand || ''} ****${p.card_last4}` : null],
    ['Captured At', p.captured_at ? fmt(p.captured_at) : null],
    ['Voided At', p.voided_at ? fmt(p.voided_at) : null],
  ].filter(([, v]) => v != null && v !== '');

  return (
    <div className={styles.tabContent}>
      {payments.length > 0 ? payments.map((p, i) => (
        <div key={i} className={styles.infoCard}>
          <h3 className={styles.cardTitle}>Payment{payments.length > 1 ? ` #${i + 1}` : ''}</h3>
          {sandboxNotice}
          {paymentFields(p).map(([k, v]) => (
            <div key={k} className={styles.infoRow}>
              <span className={styles.label}>{k}</span>
              <span className={styles.value}>{v}</span>
            </div>
          ))}
        </div>
      )) : (
        <div className={styles.infoCard}><p className={styles.emptyMessage}>No payment records</p></div>
      )}

      {(order.consultation_hold_transaction_id || order.order_hold_transaction_id) && (
        <div className={styles.infoCard}>
          <h3 className={styles.cardTitle}>Transaction Holds</h3>
          {sandboxNotice}
          {/* Hold ids hang off the ORDERS row, so they are named by `order.payment_provider` —
              ⛔ not by a payments-row provider, which is a different row and can differ. */}
          {formatProviderLabel(order.payment_provider) && (
            <div className={styles.infoRow}>
              <span className={styles.label}>Provider</span>
              <span className={styles.value}>{formatProviderLabel(order.payment_provider)}</span>
            </div>
          )}
          {order.consultation_hold_transaction_id && (
            <div className={styles.infoRow}>
              <span className={styles.label}>Consultation Hold</span>
              <span className={styles.value}>{order.consultation_hold_transaction_id}</span>
            </div>
          )}
          {order.order_hold_transaction_id && (
            <div className={styles.infoRow}>
              <span className={styles.label}>Order Hold</span>
              <span className={styles.value}>{order.order_hold_transaction_id}</span>
            </div>
          )}
        </div>
      )}

      <div className={styles.infoCard}>
        <h3 className={styles.cardTitle}>Live Payment Check</h3>
        <button className={styles.checkBtn} onClick={handleCheck} disabled={loading}>
          {loading ? 'Checking\u2026' : 'Check Live Status'}
        </button>
        {err && <div className={styles.checkError}>{err}</div>}
        {live && (live.message ? (
          <p className={styles.emptyMessage}>{live.message}</p>
        ) : (
          live.transactions?.map((tx, i) => (
            <div key={i} className={styles.liveResult}>
              <h4 className={styles.liveResultLabel}>{tx.label.replace(/_/g, ' ')} <span className={styles.txId}>({tx.transactionId})</span></h4>
              {tx.error ? (
                <div className={styles.checkError}>{tx.error}</div>
              ) : tx.parsed ? (
                <>
                  {[
                    ['Status', tx.parsed.transactionStatus],
                    ['Amount', tx.parsed.settleAmount ? `$${tx.parsed.settleAmount}` : null],
                    ['Auth Code', tx.parsed.authCode],
                    ['Settlement Date', tx.parsed.settlementDate],
                    ['AVS Response', tx.parsed.avsResponse],
                    ['CVV Response', tx.parsed.cvvResponse],
                  ].filter(([, v]) => v != null).map(([k, v]) => (
                    <div key={k} className={styles.infoRow}>
                      <span className={styles.label}>{k}</span>
                      <span className={styles.value}>{v}</span>
                    </div>
                  ))}
                </>
              ) : null}
            </div>
          ))
        ))}
      </div>
    </div>
  );
};

/* ============================================
   Activity Log Tab
   ============================================ */

const LogEntry = ({ log }) => {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = log.details && typeof log.details === 'object' && Object.keys(log.details).length > 0;
  const hasError = log.error && (typeof log.error === 'string' ? log.error.length > 0 : Object.keys(log.error).length > 0);

  return (
    <div className={styles.logEntry}>
      <div className={styles.logHeader}>
        <span className={styles.logTime}>{fmt(log.created_at)}</span>
        <span className={log.type === 'error' ? styles.logBadgeError : styles.logBadgeInfo}>
          {log.type || 'info'}
        </span>
        {log.action && <span className={styles.logAction}>{log.action}</span>}
      </div>
      {log.message && <p className={styles.logMessage}>{log.message}</p>}
      {(hasDetails || hasError) && (
        <>
          <button className={styles.expandBtn} onClick={() => setExpanded(!expanded)}>
            {expanded ? '\u25B2 Hide details' : '\u25BC Show details'}
          </button>
          {expanded && (
            <div className={styles.logDetails}>
              {hasDetails && <pre className={styles.jsonPre}>{JSON.stringify(log.details, null, 2)}</pre>}
              {hasError && <pre className={styles.jsonPreError}>{typeof log.error === 'string' ? log.error : JSON.stringify(log.error, null, 2)}</pre>}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ActivityLogTab = ({ data }) => {
  const logs = data.logs || {};
  const sections = [
    { key: 'paymentErrors', title: 'Payment Errors', entries: logs.paymentErrors || [] },
    { key: 'drCareErrors', title: 'DrCare Errors', entries: logs.drCareErrors || [] },
    { key: 'generalErrors', title: 'General Errors', entries: logs.generalErrors || [] },
    { key: 'info', title: 'Info & Events', entries: logs.info || [] },
  ].filter(s => s.entries.length > 0);

  if (sections.length === 0) {
    return <div className={styles.tabContent}><div className={styles.infoCard}><p className={styles.emptyMessage}>No log entries</p></div></div>;
  }

  return (
    <div className={styles.tabContent}>
      {sections.map(({ key, title, entries }) => (
        <div key={key} className={styles.infoCard}>
          <h3 className={styles.cardTitle}>{title}</h3>
          <div className={styles.logList}>
            {entries.map((log, i) => <LogEntry key={log.id || i} log={log} />)}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ============================================
   Main Page Component
   ============================================ */

const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try { setData(await getOrderDetail(id)); }
      catch (err) { setError(err.message || 'Failed to load order detail'); }
      finally { setLoading(false); }
    };
    load();
  }, [id]);

  if (loading) return <div className={styles.page}><div className={styles.loadingContainer}><LoadingSpinner /></div></div>;

  if (error) {
    return (
      <div className={styles.page}>
        <button className={styles.backBtn} onClick={() => navigate('/admin/orders')}>{'\u2190 Back to Orders'}</button>
        <div className={styles.errorCard}><p>{error}</p></div>
      </div>
    );
  }

  const renderTab = () => {
    switch (activeTab) {
      case 0: return <OverviewTab data={data} />;
      case 1: return <IntakeFormTab data={data} />;
      case 2: return <DrCareTab data={data} orderId={id} />;
      case 3: return <PaymentTab data={data} orderId={id} />;
      case 4: return <ActivityLogTab data={data} />;
      default: return null;
    }
  };

  return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={() => navigate('/admin/orders')}>{'\u2190 Back to Orders'}</button>
      <div className={styles.header}>
        <div>
          <h1>{g(data?.order, 'orderNumber', 'order_number') || 'Order Detail'}</h1>
          <p>{data?.order?.user?.email || ''}</p>
        </div>
        <StatusBadge status={data?.order?.status} />
      </div>
      <div className={styles.tabs}>
        {TABS.map((tab, i) => (
          <button key={tab} className={`${styles.tab} ${activeTab === i ? styles.tabActive : ''}`} onClick={() => setActiveTab(i)}>{tab}</button>
        ))}
      </div>
      {renderTab()}
    </div>
  );
};

export default OrderDetailPage;
